import {
  sendMagicLink,
  savePilotProfile,
  isSignedIn,
  getCurrentUserEmail,
} from '../server/auth';
import {
  ensureIdentity,
  getIdentity,
  HOMEWORLDS,
  normalizeHomeworld,
} from '../server/identity';
import { isBackendConfigured } from '../server/supabase';
import { isHandleAllowed } from '../server/handles';
import { attachMenuSounds } from './MenuSounds';
import { getHomeworldBadge, homeworldBadgeStyle } from './HomeworldBadges';
import type { PilotAccountStatus } from './Title';

export interface ClaimHandlers {
  onClose: () => void;
  onClaimed: () => void;
}

export class ClaimScreen {
  readonly root: HTMLDivElement;
  private readonly contentEl: HTMLDivElement;
  private status: PilotAccountStatus = 'guest';
  private email: string | null = null;

  constructor(parent: HTMLElement, private handlers: ClaimHandlers) {
    this.root = document.createElement('div');
    this.root.id = 'claim-screen';
    this.root.className = 'tst-screen tst-overlay';
    this.root.style.zIndex = '35';
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="tst-card tst-card-profile">
        <div class="tst-title-sm">PILOT PROFILE</div>
        <div class="tst-claim-body"></div>
      </div>
    `;
    this.contentEl = this.root.querySelector('.tst-claim-body') as HTMLDivElement;
    this.root.addEventListener('click', (event) => {
      if (event.target === this.root) this.handlers.onClose();
    });
    attachMenuSounds(this.root);
    parent.appendChild(this.root);
  }

  async show(): Promise<void> {
    this.root.hidden = false;
    this.contentEl.innerHTML = `<div class="tst-empty">Loading profile...</div>`;
    await ensureIdentity();
    await this.refreshAccountStatus();
    this.renderProfile();
  }

  hide(): void { this.root.hidden = true; }

  private renderProfile(message?: string): void {
    const id = getIdentity();
    const handle = id?.handle ?? '';
    const homeworld = normalizeHomeworld(id?.homeworld);
    const backendReady = isBackendConfigured();
    const badge = profileStatusLabel(this.status);
    const accountCopy = this.status === 'claimed'
      ? `Secured${this.email ? ` as ${escapeHtml(this.email)}` : ''}.`
      : backendReady
        ? 'Sign in or claim this guest pilot by email.'
        : 'Offline mode: profile changes stay on this browser until the backend is configured.';

    this.contentEl.innerHTML = `
      <div class="tst-profile-badge">${badge}</div>
      <label class="tst-field-label" for="pilot-handle">Handle</label>
      <input id="pilot-handle" type="text" data-id="handle" class="tst-input" maxlength="32" value="${escapeAttr(handle)}" autocomplete="nickname" />

      <fieldset class="tst-homeworld-picker" aria-label="Homeworld">
        <legend class="tst-field-label">Homeworld</legend>
        <div class="tst-homeworld-grid">
          ${HOMEWORLDS.map((world) => {
            const badge = getHomeworldBadge(world);
            return `
              <label class="tst-homeworld-option${world === homeworld ? ' is-selected' : ''}" data-homeworld="${escapeAttr(badge.slug)}">
                <input
                  class="tst-homeworld-radio"
                  type="radio"
                  name="pilot-homeworld"
                  data-id="homeworld"
                  value="${escapeAttr(world)}"
                  ${world === homeworld ? 'checked' : ''}
                />
                <span class="tst-homeworld-icon" style="${homeworldBadgeStyle(world)}" aria-hidden="true"></span>
                <span class="tst-homeworld-name">${escapeHtml(world)}</span>
              </label>
            `;
          }).join('')}
        </div>
      </fieldset>

      <div class="tst-error" data-id="error" hidden></div>
      <div class="tst-status tst-profile-message" data-id="message"${message ? '' : ' hidden'}>${message ? escapeHtml(message) : ''}</div>

      <div class="tst-buttons">
        <button class="tst-btn tst-btn-primary" data-action="save">Save Profile</button>
        <button class="tst-btn tst-btn-ghost" data-action="close">Close</button>
      </div>

      <div class="tst-profile-account">
        <div class="tst-card-label">Account</div>
        <p class="tst-blurb">${accountCopy}</p>
        ${this.status !== 'claimed' && backendReady ? `
          <input type="email" data-id="email" class="tst-input" placeholder="you@example.com" autocomplete="email" />
          <button class="tst-btn" data-action="send">Send Sign-In Link</button>
        ` : ''}
      </div>
    `;

    const handleInput = this.contentEl.querySelector('[data-id="handle"]') as HTMLInputElement;
    const homeworldInputs = Array.from(this.contentEl.querySelectorAll<HTMLInputElement>('input[name="pilot-homeworld"]'));
    const errorEl = this.contentEl.querySelector('[data-id="error"]') as HTMLDivElement;
    const save = this.contentEl.querySelector('[data-action="save"]') as HTMLButtonElement;
    const syncHomeworldSelection = (): void => {
      homeworldInputs.forEach((input) => {
        input.closest('.tst-homeworld-option')?.classList.toggle('is-selected', input.checked);
      });
    };
    homeworldInputs.forEach((input) => {
      input.addEventListener('change', syncHomeworldSelection);
    });

    const saveProfile = async (): Promise<boolean> => {
      errorEl.hidden = true;
      const result = await this.saveCurrent(handleInput, save);
      if (!result.ok) {
        errorEl.textContent = result.error ?? 'Failed to save profile';
        errorEl.hidden = false;
        return false;
      }
      this.handlers.onClaimed();
      await this.refreshAccountStatus();
      this.renderProfile('Profile saved.');
      return true;
    };

    save.addEventListener('click', () => { void saveProfile(); });
    handleInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') void saveProfile();
    });
    this.contentEl.querySelector('[data-action="close"]')!.addEventListener('click', this.handlers.onClose);

    const send = this.contentEl.querySelector('[data-action="send"]') as HTMLButtonElement | null;
    const emailInput = this.contentEl.querySelector('[data-id="email"]') as HTMLInputElement | null;
    if (send && emailInput) {
      send.addEventListener('click', () => {
        void this.sendAccountLink(emailInput, handleInput, save, send, errorEl);
      });
      emailInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          void this.sendAccountLink(emailInput, handleInput, save, send, errorEl);
        }
      });
    }
  }

  private async saveCurrent(
    handleInput: HTMLInputElement,
    saveButton: HTMLButtonElement,
  ): Promise<{ ok: boolean; error?: string }> {
    const handle = handleInput.value.trim();
    const homeworld = this.getSelectedHomeworld();
    const validation = validateProfile(handle);
    if (validation) return { ok: false, error: validation };

    saveButton.disabled = true;
    saveButton.textContent = 'Saving...';
    const result = await savePilotProfile(handle, homeworld);
    saveButton.disabled = false;
    saveButton.textContent = 'Save Profile';
    return result;
  }

  private async sendAccountLink(
    emailInput: HTMLInputElement,
    handleInput: HTMLInputElement,
    saveButton: HTMLButtonElement,
    sendButton: HTMLButtonElement,
    errorEl: HTMLDivElement,
  ): Promise<void> {
    errorEl.hidden = true;
    const saved = await this.saveCurrent(handleInput, saveButton);
    if (!saved.ok) {
      errorEl.textContent = saved.error ?? 'Failed to save profile';
      errorEl.hidden = false;
      return;
    }
    this.handlers.onClaimed();

    const email = emailInput.value.trim();
    if (!email || !email.includes('@')) {
      errorEl.textContent = 'Enter a valid email';
      errorEl.hidden = false;
      return;
    }

    sendButton.disabled = true;
    sendButton.textContent = 'Sending...';
    const result = await sendMagicLink(email);
    sendButton.disabled = false;
    sendButton.textContent = 'Send Sign-In Link';
    if (!result.ok) {
      errorEl.textContent = result.error ?? 'Failed to send magic link';
      errorEl.hidden = false;
      return;
    }
    this.renderProfile('Magic link sent. Open it to sign in or claim this pilot.');
  }

  private getSelectedHomeworld(): string {
    const checked = Array.from(this.contentEl.querySelectorAll<HTMLInputElement>('input[name="pilot-homeworld"]'))
      .find((input) => input.checked);
    return normalizeHomeworld(checked?.value);
  }

  private async refreshAccountStatus(): Promise<void> {
    const signedIn = await isSignedIn();
    this.email = signedIn ? await getCurrentUserEmail() : null;
    if (signedIn) {
      this.status = 'claimed';
      return;
    }
    this.status = 'guest';
  }
}

function profileStatusLabel(status: PilotAccountStatus): string {
  switch (status) {
    case 'claimed':
      return 'Claimed Pilot';
    case 'linked':
      return 'Linked Pilot';
    case 'guest':
    default:
      return 'Guest Pilot';
  }
}

function validateProfile(handle: string): string | null {
  if (handle.length < 3 || handle.length > 32) return 'Handle must be 3-32 characters';
  if (!isHandleAllowed(handle)) return 'Handle contains a banned token';
  return null;
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/"/g, '&quot;');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
